import { Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { Badge } from 'primeng/badge';
import {
  IonContent,
  IonHeader,
  IonMenu,
  IonMenuToggle,
  IonToolbar,
} from '@ionic/angular/standalone';
import * as AuthActions from '../../../store/auth/actions/auth.actions';
import { Store } from '@ngrx/store';
import { EnumNavigation } from '../../../constants';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { UserService } from '../../../service/user.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { catchError, filter, map, startWith, switchMap, combineLatestWith } from 'rxjs/operators';
import { of } from 'rxjs';
import { selectAuthToken } from '../../../store/auth/selectors/auth.selectors';
import { selectGdprConsented } from '../../../store/gdpr/selectors/gdpr.selectors';
import { JwtHelperService } from '../../../service/jwt-helper.service';
import { EnrichmentService } from '../../../service/enrichment.service';

interface MenuItem {
  label: string;
  icon: string;
  description: string;
  badge?: string;
  badgeSeverity?: 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast';
  link?: string;
  line?: boolean;
  action?: () => void;
  className?: string;
}

@Component({
  selector: 'app-slide-menu',
  standalone: true,
  imports: [IonMenu, IonHeader, IonToolbar, IonContent, IonMenuToggle, Badge, RouterLink, NgClass],
  templateUrl: './slide-menu.html',
  styleUrl: './slide-menu.css',
  encapsulation: ViewEncapsulation.None,
})
export class SlideMenu {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly jwtHelper = inject(JwtHelperService);
  private readonly enrichmentService = inject(EnrichmentService);

  // Automatically stays in sync with user updates from anywhere in the app
  public user = toSignal(this.userService.currentUser$, { initialValue: null });

  // Track current URL for active state detection
  public currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Get current user roles from JWT token
  private currentRoles = toSignal(
    this.store.select(selectAuthToken).pipe(map((token) => token?.roles || [])),
    { initialValue: [] },
  );

  // GDPR consent state — controls visual cue on Enrich & Discover menu item
  private gdprConsented = toSignal(this.store.select(selectGdprConsented), { initialValue: false });

  // Fetch enrichment badge count on each navigation (re-fetches stats when menu opens)
  private notReadyCount = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      combineLatestWith(
        this.store.select(selectAuthToken).pipe(map((token) => token?.roles || [])),
      ),
      switchMap(([, roles]) => {
        // Enrichment stats endpoint is SUPER_ADMIN-only on the backend.
        // ROLE_ADMIN must NOT call it — would 403. Gate the call here too.
        if (roles.includes('ROLE_SUPER_ADMIN')) {
          return this.enrichmentService.getStats().pipe(
            map((stats) => stats.statusCounts?.['NOT_READY'] || 0),
            catchError(() => of(0)),
          );
        }
        return of(0);
      }),
    ),
    { initialValue: 0 },
  );

  private Navigation = EnumNavigation;

  private allMenuItems = signal<MenuItem[]>([
    {
      label: 'Profile',
      icon: 'pi pi-user',
      description: 'Manage your profile',
      link: this.Navigation.PROFILES_HOME,
    },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      description: 'Options, Privacy and Permalinks',
      link: this.Navigation.SETTINGS_HOME,
    },
    {
      label: 'Enrich & Discover',
      icon: 'pi pi-search',
      description: 'Enrich contacts with external data',
      link: this.Navigation.ENRICHMENT_HOME,
      line: true,
      className: 'pb-2',
    },
    {
      label: 'Home',
      icon: 'pi pi-home',
      description: 'Dashboard for app',
      link: this.Navigation.DASHBOARD_HOME,
      className: 'pt-2',
    },
    {
      label: 'Chat',
      icon: 'pi pi-comments',
      description: 'Talk to the AI assistant',
      link: this.Navigation.CHAT_HOME,
    },
    {
      label: 'Contacts',
      icon: 'pi pi-address-book',
      description: 'All Contacts for app',
      link: this.Navigation.CONTACTS_HOME,
    },
    {
      label: 'Restaurants',
      icon: 'pi pi-shop',
      description: 'Restaurants for app',
      link: this.Navigation.LOCATIONS_HOME,
    },
    {
      label: 'Activity',
      icon: 'pi pi-chart-bar',
      description: 'View analytics and activity reports',
      link: this.Navigation.ACTIVITY_HOME,
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      description: 'Sign out of your account',
      action: () => this.store.dispatch(AuthActions.logout()),
      className: 'pt-2',
    },
  ]);

  /**
   * Filtered menu items based on user roles
   * Settings is only visible to ADMIN and SUPER_ADMIN users
   */
  menuItems = computed(() => {
    const roles = this.currentRoles();
    const notReady = this.notReadyCount();
    const consented = this.gdprConsented();
    const isSuperAdmin = roles.includes('ROLE_SUPER_ADMIN');
    const isAdminOrAbove = isSuperAdmin || roles.includes('ROLE_ADMIN');
    return this.allMenuItems()
      .filter((item) => {
        if (item.label === 'Settings' || item.label === 'Enrich & Discover') {
          return isSuperAdmin;
        }
        if (item.label === 'Home') {
          return isAdminOrAbove;
        }
        return true;
      })
      .map((item) => {
        if (item.label === 'Enrich & Discover') {
          if (!consented) {
            // GDPR consent missing — show lock icon and a warning badge so the
            // user understands the feature is gated. The route guard still
            // enforces the redirect and surfaces a notification on click.
            return {
              ...item,
              icon: 'pi pi-lock',
              badge: 'Consent',
              badgeSeverity: 'warn' as const,
              description: 'GDPR consent required to access enrichment',
            };
          }
          if (notReady > 0) {
            return { ...item, badge: notReady.toString(), badgeSeverity: 'danger' as const };
          }
        }
        return item;
      });
  });

  /**
   * Check if a menu item is currently active based on the router URL
   */
  isActive(item: MenuItem): boolean {
    if (!item.link) return false;
    const currentUrl = this.currentUrl();
    if (!currentUrl) return false;

    // Ensure both URLs start with / for consistent comparison
    const normalizedLink = item.link.startsWith('/') ? item.link : `/${item.link}`;

    // Extract the path without query params or fragments
    const currentPath = currentUrl.split('?')[0].split('#')[0];

    // Exact match only
    return currentPath === normalizedLink;
  }

  /**
   * Get icon color class based on active state
   */
  getIconColorClass(item: MenuItem): string {
    return this.isActive(item)
      ? 'text-primary dark:text-primary'
      : 'text-surface-500 dark:text-surface-300';
  }
}
